/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useMemo } from 'react';

import { WorkspaceSubMenu as BaseWorkspaceSubMenu } from '@adp-foundation/space-ui-base';
import { useSpaceStore } from '@adp-foundation/space-store';
import { SpaceRoleType } from '@adp-arch/idl/developer_api';
import { I18n } from '@adp-arch/i18n';
import {
  IconCozBot,
  IconCozBotFill,
  IconCozDocument,
  IconCozDocumentFill,
  IconCozKnowledge,
  IconCozKnowledgeFill,
  IconCozSetting,
  IconCozSettingFill,
  IconCozClock,
  IconCozClockFill,
} from '@coze-arch/coze-design/icons';
import { useRouteConfig } from '@adp-arch/bot-hooks';

import { SpaceSubModuleEnum } from '@/const';

import { WorkspaceSwitcher } from '../workspace-switcher';

export const WorkspaceSubMenu = () => {
  const { subMenuKey } = useRouteConfig();
  const currentSpace = useSpaceStore(state => state.space);

  // Check if current user has space management permission (Owner or Admin)
  const hasSpaceManagePermission = useMemo(() => {
    const roleType = currentSpace?.space_role_type;
    return roleType === SpaceRoleType.Owner || roleType === SpaceRoleType.Admin;
  }, [currentSpace?.space_role_type]);

  const subMenu = useMemo(() => {
    const menus = [
      {
        icon: <IconCozBot />,
        activeIcon: <IconCozBotFill />,
        title: () => I18n.t('navigation_workspace_develop', {}, 'Develop'),
        path: SpaceSubModuleEnum.DEVELOP,
        dataTestId: 'navigation_workspace_develop',
      },
    ];

    // Only show Library menu for Owner and Admin (not for Members)
    if (hasSpaceManagePermission) {
      menus.push({
        icon: <IconCozKnowledge />,
        activeIcon: <IconCozKnowledgeFill />,
        title: () => I18n.t('navigation_workspace_library', {}, 'Library'),
        path: SpaceSubModuleEnum.LIBRARY,
        dataTestId: 'navigation_workspace_library',
      });

      // Only show Space Settings menu for Owner and Admin
      menus.push({
        icon: <IconCozSetting />,
        activeIcon: <IconCozSettingFill />,
        title: () => I18n.t('navigation_workspace_team', {}, 'Space Settings'),
        path: SpaceSubModuleEnum.TEAM,
        dataTestId: 'navigation_workspace_team',
      });
      menus.push({
        icon: <IconCozClock />,
        activeIcon: <IconCozClockFill />,
        title: () =>
          I18n.t('navigation_workspace_chat_history', {}, 'Chat History'),
        path: SpaceSubModuleEnum.CHAT_HISTORY,
        dataTestId: 'navigation_workspace_chat_history',
      });
      menus.push({
        icon: <IconCozDocument />,
        activeIcon: <IconCozDocumentFill />,
        title: () => 'Token Usage',
        path: SpaceSubModuleEnum.TOKEN_USAGE,
        dataTestId: 'navigation_workspace_token_usage',
      });
    }

    return menus;
  }, [hasSpaceManagePermission]);

  const headerNode = <WorkspaceSwitcher />;

  return (
    <BaseWorkspaceSubMenu
      header={headerNode}
      menus={subMenu}
      currentSubMenu={subMenuKey}
    />
  );
};
